import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, Star } from "lucide-react";
import { COMPANY } from "@/data/content";
import { scrollToTarget } from "@/components/site/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1604413950933-073730fba9f2?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = [
  "West Michigan's Trusted",
  "Masonry & Chimney",
  "Experts for 30+ Years",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-charcoal">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Brick home with restored chimney in West Michigan" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/55 to-charcoal/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 mb-6"
        >
          <span className="flex text-brick-light">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-current" />
            ))}
          </span>
          <span className="text-white/90 text-xs font-semibold tracking-wide">Family-owned & trusted for 30+ years</span>
        </motion.div>

        <h1 data-testid="hero-headline" className="font-heading font-extrabold text-white tracking-tight text-4xl sm:text-6xl md:text-7xl leading-[1.02] max-w-4xl">
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden py-0.5">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {line.includes("Masonry") ? (
                  <>Masonry <span className="text-brick-light">& Chimney</span></>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-6 text-white/85 text-base sm:text-lg max-w-2xl leading-relaxed"
        >
          BBS Masonry specializes in quality masonry work, chimney repair, brick repair, stonework and
          waterproofing — backed by honest pricing and free estimates for homeowners across Michigan.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-estimate-btn"
            onClick={() => scrollToTarget("#estimate")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brick hover:bg-brick-dark text-white font-bold text-base px-8 py-4 transition-[background-color,transform] duration-200 hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-brick focus:ring-offset-2 focus:ring-offset-charcoal"
          >
            Request a Free Estimate
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            data-testid="hero-call-btn"
            href={COMPANY.phoneHref}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/40 text-white hover:bg-white hover:text-charcoal font-bold text-base px-8 py-4 transition-[background-color,color] duration-200"
          >
            <Phone className="w-5 h-5" /> Call {COMPANY.phoneDisplay}
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-white/50 text-[10px] tracking-[0.2em] uppercase">Scroll</span>
        <div className="w-px h-8 bg-white/30 overflow-hidden">
          <motion.div animate={{ y: [-32, 32] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} className="w-full h-4 bg-brick-light" />
        </div>
      </motion.div>
    </section>
  );
};
